// Permission utility functions

/**
 * Check if a user has a specific permission
 * @param {Object} user - The current user object
 * @param {string} permission - The permission to check (e.g., 'delete_tasks', 'delete_empresas')
 * @param {Array} roles - Array of role objects from the database
 * @returns {boolean} - True if user has the permission
 */
export const hasPermission = (user, permission, roles = []) => {
  if (!user) return false;
  
  // Admin/administrator has all permissions
  const userRole = user.role?.toLowerCase();
  if (userRole === 'admin' || userRole === 'administrator' || userRole === 'administrador') {
    return true;
  }
  
  // Check user's permissions array if it exists
  if (user.permissions && Array.isArray(user.permissions)) {
    if (user.permissions.includes('all')) return true;
    if (user.permissions.includes(permission)) return true;
  }
  
  // Find user's role in roles array and check permissions
  const role = roles.find(r => 
    r.name?.toLowerCase() === userRole || 
    r.id === user.role_id
  );
  
  if (role && role.permissions) {
    if (role.permissions.includes('all')) return true;
    if (role.permissions.includes(permission)) return true;
  }
  
  return false;
};

/**
 * Check if user can delete a specific resource type
 * @param {Object} user - The current user object  
 * @param {string} resourceType - Type of resource ('tasks', 'empresas', 'notes', 'deals')
 * @param {Array} roles - Array of role objects
 * @returns {boolean}
 */
export const canDelete = (user, resourceType, roles = []) => {
  return hasPermission(user, `delete_${resourceType}`, roles);
};

/**
 * Default permissions for different roles (used when role permissions aren't loaded)
 */
export const defaultRolePermissions = {
  'administrador': ['all'],
  'administrator': ['all'],
  'admin': ['all'],
  'supervisor': ['view_all', 'edit_team', 'reports', 'delete_tasks', 'delete_notes'],
  'vendedor': ['view_own', 'edit_own']
};

/**
 * Get effective permissions for a user
 */
export const getEffectivePermissions = (user, roles = []) => {
  if (!user) return [];
  
  const userRole = user.role?.toLowerCase();
  
  // Check default permissions first
  if (defaultRolePermissions[userRole]) {
    const defaultPerms = defaultRolePermissions[userRole];
    if (defaultPerms.includes('all')) {
      return ['all', 'delete_tasks', 'delete_empresas', 'delete_notes', 'delete_deals'];
    }
    return defaultPerms;
  }
  
  // Find role in roles array
  const role = roles.find(r => 
    r.name?.toLowerCase() === userRole || 
    r.id === user.role_id
  );
  
  return role?.permissions || [];
};
